import React, { useRef } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import { updateOfficial, OfficialData } from '../../../networkRequest/officialsService';
import { EditGridPromiseInfo, RowMutations } from '../../../hooks/dataGridEditing/UseRowEdit';
import { useUser } from '../../../hooks/useUser';
import { AgencyOfficialRolesMap } from '../';
import { PrimaryButton, SecondaryButton } from '@components';
import ScreenReaderTitle from '../../../components/confirmation/ScreenReaderTitle';

type EditAgencyOfficialsDialogProps = {
  open: boolean;
  editOfficialPromiseInfo: EditGridPromiseInfo | null;
  setEditOfficialPromiseInfo: React.Dispatch<React.SetStateAction<EditGridPromiseInfo | null>>;
  rowMutations: RowMutations;
  setErrorMessage: React.Dispatch<React.SetStateAction<string>>;
  setSuccessMessage: React.Dispatch<React.SetStateAction<string>>;
  resetRowMutations: () => void;
  saveSingleOfficialEdit: (OfficialData) => void;
  rolesMap: AgencyOfficialRolesMap;
  onDeleteUser: () => void;
};
export const EditAgencyOfficialsDialog = ({
  open,
  editOfficialPromiseInfo,
  setEditOfficialPromiseInfo,
  saveSingleOfficialEdit,
  rowMutations,
  setErrorMessage,
  setSuccessMessage,
  resetRowMutations,
  rolesMap,
  onDeleteUser,
}: EditAgencyOfficialsDialogProps) => {
  const { user } = useUser();

  const noButtonRef = useRef<HTMLButtonElement>(null);
  const { editedKey, originalLabel, updatedLabel, updatedValue } = rowMutations;
  const isDeleteUser = editedKey === 'status' && updatedValue === 'deactivated';

  const handleEnter = () => {
    if (!user) {
      throw new Error('User not found.');
    }
    // Force the focus to 'no' for UX purposes, see MUI docs for more information
    noButtonRef.current?.focus();
  };

  const handleYes = async () => {
    if (!user) {
      throw new Error('User not found');
    }

    if (!editOfficialPromiseInfo) {
      throw new Error('Error updating official.');
    }
    const { updatedRow, originalRow, resolve } = editOfficialPromiseInfo;

    type OfficialDataPartial = Partial<OfficialData>;
    type OfficialUpdate = OfficialDataPartial & { agency_official_type_id?: number } & {
      role_id?: number;
    };

    const officialUpdates: OfficialUpdate = {};
    switch (editedKey) {
      // for future refactoror, we can probably save the role id to the data we feed to the data grid via official formatter and tag the name on in the form of a label, assuming there's no custom component being rendered.
      case 'role_name':
        officialUpdates.role_id = rolesMap.get(updatedValue as string);
        break;
      default:
        officialUpdates[editedKey] = updatedValue;
    }

    officialUpdates.id = updatedRow.id;

    try {
      const { data, error } = await updateOfficial(user.accessToken, {
        ...officialUpdates
      });
      if (data) {
        if (isDeleteUser) {
          onDeleteUser();
        } else {
          saveSingleOfficialEdit(data);
        }

        setSuccessMessage(
          `Updated ${originalLabel} to ${updatedLabel} successfully.`
        );

        resolve(data);
      } else if (error) {
        console.log('error', error);
        setErrorMessage('Error updating official. ' + error);
        // Revert the cell to its original value and settle the promise so the
        // grid exits edit mode. Without this the processRowUpdate promise stays
        // pending forever and the cell gets stuck in an active edit state.
        resolve(originalRow);
      }
    } catch (error: any) {
      setErrorMessage(error.message);
      resolve(originalRow);
    }
    resetRowMutations();
    setEditOfficialPromiseInfo(null);
  };

  const handleNo = () => {
    if (!editOfficialPromiseInfo) throw new Error('Error editing official.');
    const { originalRow, resolve } = editOfficialPromiseInfo;
    resolve(originalRow);
    resetRowMutations();
    setEditOfficialPromiseInfo(null);
  };

  const message = isDeleteUser
    ? 'This will permanently delete the user. Are you sure?'
    : `Pressing 'Yes' will change ${originalLabel} to ${updatedLabel ?? updatedValue}. Are you sure?`

  return (
    <Dialog
      data-testid="confirm-dialog"
      slotProps={{
        backdrop: { invisible: true },
        paper: { style: { borderRadius: 8 } },
        transition: { onEntered: handleEnter }
      }}
      maxWidth="md"
      open={open}
      aria-labelledby="edit-confirmation-dialog-title">
      <DialogTitle sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <ScreenReaderTitle
          accessibleTitle="Edit Confirmation"
          sanitizedTitle="edit-confirmation-dialog-title"
        />
        <ErrorOutlineOutlinedIcon sx={{ fontSize: '28pt' }} />
      </DialogTitle>
      <DialogContent>{message}</DialogContent>
      <DialogActions sx={{ padding: 2 }}>
        <SecondaryButton referenceElement={noButtonRef} action={handleNo}>
          Cancel
        </SecondaryButton>
        <PrimaryButton action={handleYes} disabled={false} loading={false}>
          Yes
        </PrimaryButton>
      </DialogActions>
    </Dialog>
  );
};

export default EditAgencyOfficialsDialog;
